import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItem from 'LuxKit/List/ListItem'
import { formatMinutesToHoursMinutes, secondsToMinutes } from 'lib/datetime/time'
import type { TravelMode } from 'tripPlanner/api/routes/getRouteInfo'
import type { TravelData } from './travelDuration'
import { getTravelModeIcon } from './travelDuration'
import GeoContext from 'contexts/geoContext'
import { formatDistance } from 'tripPlanner/utils/geo'

interface Props {
  travelDataArray: Array<TravelData | null>
  onTravelModeSelect: (travelMode: TravelMode) => void
  selectedTravelMode: TravelMode | null
  currentTravelPreference: string | undefined
  toHotel?: boolean
  fromHotel?: boolean
}

const formatTravelLabel = (intl: IntlShape, travelData: TravelData, toHotel?: boolean, fromHotel?: boolean, currentRegionCode?: string): string => {
  const { travelMode, duration, distance } = travelData

  if (!duration) {
    const modeName = travelMode === 'DRIVE'
      ? intl.formatMessage({
          id: 'ae0sc8HOG2wR1QIqS-vq',
          defaultMessage: 'Drive',
          description: 'Label for the driving travel mode option in the trip planner directions display',
        })
      : intl.formatMessage({
          id: '1zc2Kb2Pg4Cb0mR3sW6Z',
          defaultMessage: 'Walk',
          description: 'Label for the walking travel mode option in the trip planner directions display',
        })
    return intl.formatMessage(
      {
        id: 'x2sYs0mM2c6V5s6CvH1P',
        defaultMessage: '{travelMode} unavailable',
        description: 'Label when a specific travel mode has no data available',
      },
      { travelMode: modeName },
    )
  }

  const durationInMinutes = secondsToMinutes(parseInt(duration.replace('s', '')))
  const formattedDuration = formatMinutesToHoursMinutes(durationInMinutes, intl)
  const formattedDistance = formatDistance(distance, currentRegionCode || 'AU')

  if (toHotel) {
    return intl.formatMessage(
      {
        id: 'LODERv3k5f7qsRTfM4Ty',
        defaultMessage: '{duration} \u00b7 {distance} to hotel',
        description: 'Travel duration with hotel destination in the dropdown list',
      },
      { duration: formattedDuration, distance: formattedDistance },
    )
  }
  if (fromHotel) {
    return intl.formatMessage(
      {
        id: '3DkS6B4dC42ZjKhNiPeO',
        defaultMessage: '{duration} \u00b7 {distance} from hotel',
        description: 'Travel duration from hotel in the dropdown list in the trip planner timeline view',
      },
      { duration: formattedDuration, distance: formattedDistance },
    )
  }
  return intl.formatMessage(
    {
      id: 'KwEU8C4PnLqLIO1leSn8',
      defaultMessage: '{duration} \u00b7 {distance}',
      description: 'Travel duration between locations in the dropdown list',
    },
    { duration: formattedDuration, distance: formattedDistance },
  )
}

function TravelModeList({
  travelDataArray,
  onTravelModeSelect,
  selectedTravelMode,
  currentTravelPreference,
  toHotel,
  fromHotel,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const handleTravelModeSelect = useCallback(
    (travelMode: TravelMode) => () => {
      onTravelModeSelect(travelMode)
    },
    [onTravelModeSelect],
  )
  return (
    <>
      {travelDataArray
        .filter(Boolean)
        .map(modeData => {
          if (!modeData) return null

          if (modeData.duration) {
            return (
              <ClickableListItem
                key={modeData.travelMode}
                title={formatTravelLabel(intl, modeData, toHotel, fromHotel, currentRegionCode)}
                startIcon={getTravelModeIcon(modeData.travelMode)}
                onClick={handleTravelModeSelect(modeData.travelMode)}
                selected={selectedTravelMode === modeData.travelMode || (!selectedTravelMode && currentTravelPreference === modeData.travelMode)}
              />
            )
          }

          return (
            <ListItem
              key={modeData.travelMode}
              title={intl.formatMessage({
                id: '0uuD6fojRl80J9XUTw35',
                defaultMessage: 'Unavailable',
                description: 'Label for a travel mode option that has no data available',
              })}
              startIcon={getTravelModeIcon(modeData.travelMode, 'neutral-four')}
              disabled
              titleColour="neutral-four"
            />
          )
        })}
    </>
  )
}

export default TravelModeList
